<!--
  CollectorLineAnswer — her answer, live on the collector's line (collector-app
  P3-C, L-E / L-F; mockup Line.dc.html card «ج»).

  The green block «ردّت 11:31: «{her words}» — {instruction}» and the two
  REPORTS under it: «اشتريته» (`bought`) and «مش لقيته» (`not_found`). They are
  reports, not marks: the line stays parked on her question and the leader's
  one-tap close at the station applies her option and bills — nothing here
  ever sets the line's outcome. `instruction` is the server's sentence and is
  printed as sent.

  Buttons only for a YES (nothing to buy on «متشتريهوش — استنى الليدر» or
  «سيبه النهارده»; those read in amber with no buttons). After a report the
  block says so — «بلّغت الليدر إنك اشتريته — هيقفلها في المقر» — the chosen
  button is held down, and the other stays live: the backend takes a changed
  mind (the leader reads the LAST report).

  `part` places it: `all` is the card (block + buttons), `text` the sheet's
  body, `buttons` the sheet's fixed footer (52 px, like the mark buttons).
  Everything it says comes from collector/lineView.js `answerView`.
-->
<template>
  <div
    v-if="view?.answered && part !== 'buttons'"
    class="c-answer"
    :class="view.canReport ? 'c-answer--go' : 'c-answer--stop'"
    :data-response="view.response"
  >
    <div class="c-answer__said">
      {{ saidText }}
    </div>
    <div v-if="view.reported" class="c-answer__done">
      {{ t(`collector.answer.reported.${view.reported}`) }}
    </div>
    <div v-if="part === 'all' && view.canReport" class="c-answer__acts">
      <button
        v-for="type in TYPES"
        :key="type"
        type="button"
        class="c-reset c-answer__btn"
        :class="[`c-answer__btn--${type}`, { 'c-answer__btn--on': view.reported === type }]"
        :disabled="view.reported === type"
        @click="emit('report', line, type)"
      >
        {{ buttonLabel(type) }}
      </button>
    </div>
  </div>
  <div v-else-if="view?.answered && view.canReport" class="c-answer-btns">
    <button
      v-for="type in TYPES"
      :key="type"
      type="button"
      class="c-reset c-answer-btns__btn"
      :class="[`c-answer-btns__btn--${type}`, { 'c-answer-btns__btn--on': view.reported === type }]"
      :disabled="view.reported === type"
      @click="emit('report', line, type)"
    >
      {{ buttonLabel(type) }}
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { answerSaidPhrase, answerView } from '@/collector/lineView.js';

const props = defineProps({
  line: { type: Object, required: true },
  /** 'all' | 'text' | 'buttons' — which part of the answer this placement draws. */
  part: { type: String, default: 'all' },
});
const emit = defineEmits(['report']);

const { t } = useI18n();

/** The two reports, in the order the buttons stand. */
const TYPES = ['bought', 'not_found'];

const view = computed(() => answerView(props.line));

/** «ردّت 11:31: «أي لون» — هات أسود بـ 135» */
const saidText = computed(() => {
  const phrase = answerSaidPhrase(view.value);
  return t(phrase.key, phrase.params);
});

/** The held-down button wears a ✓. */
function buttonLabel(type) {
  const label = t(`collector.answer.${type}`);
  return view.value?.reported === type ? `✓ ${label}` : label;
}
</script>

<style scoped>
.c-answer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border-radius: var(--c-radius-chip);
}

.c-answer--go { background: var(--c-green-fill); color: var(--c-green-text); }
.c-answer--stop { background: var(--c-amber-fill); color: var(--c-amber-text); }

.c-answer__said {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
}

.c-answer__done {
  font-size: 12px;
  font-weight: 700;
  color: var(--c-ink-soft);
}

.c-answer__acts {
  display: flex;
  gap: 8px;
}

.c-answer__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  height: 44px;
  border-radius: var(--c-radius-chip);
  font-size: 15px;
  font-weight: 700;
  box-sizing: border-box;
}

.c-answer__btn--bought { background: var(--c-green-text); color: #ffffff; }
.c-answer__btn--not_found { border: 2px solid var(--c-red-border); background: var(--c-card); color: var(--c-red-text); }
.c-answer__btn--on { opacity: 0.7; }
.c-answer__btn--not_found.c-answer__btn--on { background: var(--c-red-fill); }

.c-answer-btns {
  display: flex;
  gap: 10px;
}

.c-answer-btns__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  height: 52px;
  border-radius: var(--c-radius-button);
  font-size: 17px;
  font-weight: 700;
  box-sizing: border-box;
}

.c-answer-btns__btn--bought { background: var(--c-green-text); color: #ffffff; }
.c-answer-btns__btn--not_found { border: 2px solid var(--c-red-border); background: var(--c-card); color: var(--c-red-text); }
.c-answer-btns__btn--on { opacity: 0.7; }
.c-answer-btns__btn--not_found.c-answer-btns__btn--on { background: var(--c-red-fill); }
</style>
